/* eslint-disable @typescript-eslint/no-explicit-any */
import { resolveErrorMessage } from '@kidshome/api-contract';

/**
 * Translate a DYNAMIC key (one computed at runtime — RFC 9457 error codes, Zod message keys, theme
 * registry `labelKey`s). react-i18next's `t` is typed to the static key union, so a runtime string
 * is rejected; this thin wrapper is the single sanctioned escape hatch. Callers still pass real keys
 * that exist in the catalogs — the parity test + `i18next-parser` guard coverage.
 *
 * `t` is typed with `any` parameters so the heavily-overloaded `TFunction` from any
 * `useTranslation([...])` selection is assignable; the cast is isolated to this one boundary.
 */
export function tDynamic(
  t: (key: any, options?: any) => unknown,
  key: string,
  params?: Record<string, unknown>,
): string {
  return String(t(key, params));
}

/**
 * Resolve an API error into the `[key, params]` tuple `tDynamic` expects, ready to spread:
 * `setError(tDynamic(t, ...resolveErrParts(e)))`. Centralizes the RFC 9457 error → translatable-key
 * mapping so every error surface reads the same.
 */
export function resolveErrParts(e: unknown): [string, Record<string, unknown>] {
  const { key, params } = resolveErrorMessage(e);
  return [key, params];
}

/**
 * Resolve a PLAY-CONTENT key that may arrive namespace-qualified (the backend authors hero content
 * keys as `play.archetype.royal.stage.0`, `play.voice.royal.chore.approved`, …). Since the kid surfaces
 * translate within the `play` namespace (`useTranslation('play')`), a leading `play.` would double-prefix
 * and leak the raw key — so we strip it here. Use this for ANY `nameKey` / `taglineKey` / stage name that
 * comes from the play API. (Mirrors the same strip in the narrator's `render-voice`.)
 */
export function tPlayKey(
  t: (key: any, options?: any) => unknown,
  key: string,
  params?: Record<string, unknown>,
): string {
  return tDynamic(t, key.replace(/^play\./, ''), params);
}
